/**
 * `39-message-editor.png`: "Type your message to personalize it with fields from your board",
 * a textarea, an "Auto populate fields from board items" chip row, and Cancel / **Done**
 * (inert until the message is non-empty).
 *
 * The chips insert monday's own display tokens; the engine interpolates the storage form
 * (`{pulse.name}`, `{pulse.<columnId>}`, `{board.name}` — `specs/M6-A1` §5.7), so the two
 * shapes are translated here and nowhere else.
 */
import { useMemo, useRef, useState } from 'react';
import type { SlotEditorProps } from './pickers';
import type { PickerColumnView } from '../types';

export interface MessageToken {
  /** What the chip says. */
  readonly label: string;
  /** What is inserted into the message. */
  readonly token: string;
}

/** The three fixed chips, then one per column — monday's own order in the capture. */
export function messageTokens(columns: readonly PickerColumnView[]): MessageToken[] {
  return [
    { label: 'Item Name', token: '{pulse.name}' },
    { label: 'Board Name', token: '{board.name}' },
    { label: 'User Name', token: '{user.name}' },
    { label: 'Group Name', token: '{group.name}' },
    ...columns.filter((column) => !column.isSubitemColumn).map((column) => ({ label: column.title, token: `{pulse.${column.id}}` })),
  ];
}

export function MessageEditor({ value, onChange, close, pickers }: SlotEditorProps) {
  const [draft, setDraft] = useState(typeof value === 'string' ? value : '');
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const tokens = useMemo(() => messageTokens(pickers.columns), [pickers.columns]);

  const insert = (token: string): void => {
    const area = areaRef.current;
    const at = area?.selectionStart ?? draft.length;
    const next = `${draft.slice(0, at)}${token}${draft.slice(area?.selectionEnd ?? at)}`;
    setDraft(next);
    requestAnimationFrame(() => {
      area?.focus();
      area?.setSelectionRange(at + token.length, at + token.length);
    });
  };

  return (
    <div className="ez-automation-message" data-testid="message-editor">
      <div className="ez-automation-message__title">Type your message to personalize it with fields from your board</div>
      <textarea
        ref={areaRef}
        className="ez-automation-message__area"
        aria-label="Message"
        data-testid="message-editor-text"
        value={draft}
        autoFocus
        onChange={(event) => setDraft(event.target.value)}
      />
      <div className="ez-automation-message__fields">
        <div className="ez-automation-message__fields-title">Auto populate fields from board items</div>
        <div className="ez-automation-message__chips">
          {tokens.map((token) => (
            <button
              key={token.token}
              type="button"
              className="ez-automation-chip"
              data-testid={`message-token-${token.token.replace(/[{}.]/g, '')}`}
              onClick={() => insert(token.token)}
            >
              {token.label}
            </button>
          ))}
        </div>
      </div>
      <div className="ez-automation-message__actions">
        <button type="button" className="ez-automation-button ez-automation-button--ghost" onClick={close} data-testid="message-cancel">
          Cancel
        </button>
        <button
          type="button"
          className="ez-automation-button"
          disabled={draft.trim().length === 0}
          data-testid="message-done"
          onClick={() => {
            onChange(draft);
            close();
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
